import { useState } from "react";
import { Chevron } from "../../../shared/phone/Row";
import type { IdentityCheck } from "../../../shared/goals/profile";
import type { LearnAnswer, RunInfo } from "../../lib/productsApi";
import { host, mmss, parseTime } from "./text";

const runSecs = (r: RunInfo) =>
  r.started_at && r.ended_at ? (parseTime(r.ended_at).getTime() - parseTime(r.started_at).getTime()) / 1000 : null;

/** "Which business is yours?": the businesses the agent found with this name, and the owner's pick. */
export function AskCard({ q, run, busy, onAnswer }: { q: IdentityCheck; run: RunInfo | null; busy: boolean; onAnswer: (a: LearnAnswer) => void }) {
  const [none, setNone] = useState(false);
  const [site, setSite] = useState("");
  const secs = run ? runSecs(run) : null;
  return (
    <section className="card psec">
      <div className="card-h">
        <h3>Businesses with this name</h3>
        {run && secs !== null && (
          <span className="sub mono">
            {mmss(secs)}
          </span>
        )}
      </div>
      <div className="rows">
        {q.candidates.map((c, k) => (
          <div className="cand" key={k}>
            <div>
              <b>{c.name}</b>
              {k === q.best_guess && (
                <>
                  {" "}
                  <span className="chip ok">Its best guess</span>
                </>
              )}
              <p>{c.what_it_is}</p>
              <p className="why">{c.why}</p>
              <span className="chip">{host(c.website)}</span>
            </div>
            <button type="button" className={k === q.best_guess ? "btn sm" : "btn sm ghost"} disabled={busy} onClick={() => onAnswer({ pick: k })}>
              This is us
            </button>
          </div>
        ))}
      </div>
      {none ? (
        <div className="ask-none">
          <label className="fl" htmlFor="ask-site">
            Your website
          </label>
          <input className="field" id="ask-site" placeholder="https://" inputMode="url" value={site} onChange={(e) => setSite(e.target.value)} autoFocus />
          <div className="m-act">
            <button type="button" className="btn sm" disabled={busy || !site.trim()} onClick={() => onAnswer({ website: site.trim() })}>
              Learn from this website
            </button>
            <button type="button" className="btn sm ghost" disabled={busy} onClick={() => onAnswer({ none: true })}>
              We have no website yet
            </button>
            <button type="button" className="btn sm ghost" onClick={() => setNone(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="m-act">
          <button type="button" className="btn sm ghost" onClick={() => setNone(true)}>
            None of these
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * "Which business is yours?" on a phone or an iPad: each business it found as a group, with This is us under
 * it, and None of these for the owner's own website or none.
 */
export function AskNative({ q, run, busy, onAnswer }: { q: IdentityCheck; run: RunInfo | null; busy: boolean; onAnswer: (a: LearnAnswer) => void }) {
  const [none, setNone] = useState(false);
  const [site, setSite] = useState("");
  const secs = run ? runSecs(run) : null;
  return (
    <>
      <section className="pd-cands">
        <div className="pn-sh">
          <h2>Businesses with this name</h2>
          {secs !== null && <span className="pd-shn">{mmss(secs)}</span>}
        </div>
        {q.candidates.map((c, k) => (
          <div className="pn-grp" key={k}>
            <div className="pn-row static wrap">
              <span className="m">
                <span className="t">{c.name}</span>
                {k === q.best_guess && (
                  <span className="pd-gtop">
                    <span className="pn-pill good">Its best guess</span>
                  </span>
                )}
                <span className="s">{c.what_it_is}</span>
                <span className="s">{c.why}</span>
                <span className="s pd-host">{host(c.website)}</span>
              </span>
            </div>
            <button type="button" className="pn-row accent" disabled={busy} onClick={() => onAnswer({ pick: k })}>
              <span className="m">
                <span className="t">This is us</span>
              </span>
            </button>
          </div>
        ))}
      </section>
      {none ? (
        <section>
          <div className="pn-grp">
            <div className="pn-irow col">
              <label htmlFor="ask-site">Your website</label>
              <input id="ask-site" placeholder="https://" inputMode="url" autoCapitalize="off" autoCorrect="off" value={site} onChange={(e) => setSite(e.target.value)} />
            </div>
          </div>
          <div className="pn-pad pd-askbtns">
            <button type="button" className="pn-btn pri wide" disabled={busy || !site.trim()} onClick={() => onAnswer({ website: site.trim() })}>
              Learn from this website
            </button>
            <button type="button" className="pn-btn wide" disabled={busy} onClick={() => onAnswer({ none: true })}>
              We have no website yet
            </button>
            <button type="button" className="pn-btn ghost wide" onClick={() => setNone(false)}>
              Cancel
            </button>
          </div>
        </section>
      ) : (
        <div className="pn-grp">
          <button type="button" className="pn-row" onClick={() => setNone(true)}>
            <span className="m">
              <span className="t">None of these</span>
            </span>
            <Chevron />
          </button>
        </div>
      )}
    </>
  );
}
